'use client';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { GRID_ENABLED } from '../connections/lib/daily-game';
import {
  ALL_GAMES,
  GAME_META,
  fetchProgress,
  prettyDate,
  type DailyGame,
} from './dailyGames';

// Reverse-chronological slate for the /games hub — one row per past day's game
// across all four games (each date runs exactly one game), newest-first. Shows
// BOTH completed games (with a visual score) and ones you haven't finished yet
// (with a Play prompt), so it doubles as your scorecard and a to-do list.
//
// Per-player and per-device (it reads the localStorage session keys), so it's a
// client component fetching every game's /progress endpoint. Signed-in players
// get status across devices via X-User-Id; logged-out players get this device.

const COLLAPSED_COUNT = 24;
// Shared /9 scale, except Duel (9 duels + 2-point Call = /11).
function scoreMax(game: DailyGame): number {
  return game === 'duel' ? 11 : 9;
}

interface Row {
  game: DailyGame;
  date: string;
  played: boolean;
  score: number | null;
  solved: boolean;
  label?: string;
  image?: string;
}

// Per-game tile viz geometry for Decode/Grid (single-colour motif grid). SPOT
// shows its round photo and Connections shows the four difficulty bands, so
// they're special-cased in TileViz rather than driven by this map.
const MOTIF: Record<DailyGame, { cols: number; rows: number }> = {
  spot: { cols: 1, rows: 1 },
  connections: { cols: 4, rows: 4 },
  decode: { cols: 6, rows: 3 },
  grid: { cols: 3, rows: 3 },
  duel: { cols: 2, rows: 4 }, // two columns = the two cars per duel
};

// Car Connections' four difficulty colours (yellow easiest → purple hardest),
// matching the /games play-today ConnectionsCard bands.
const CONNECTIONS_BANDS = ['#f4c430', '#5aa469', '#4a78c4', '#8a63b8'];

// Decode's guess-feedback heat (red = off, amber = close, green = exact),
// rows warming toward a solved final row — mirrors the play-today DecodeHeat.
const DECODE_COLORS = { g: '#3a9d5d', a: '#d8a72b', r: '#b3433a' } as const;
// Four rows to mirror the Connections tile's four bands; warms toward a solved
// all-green final row.
const DECODE_HEAT: ('g' | 'a' | 'r')[][] = [
  ['r', 'r', 'a', 'r', 'a', 'r'],
  ['a', 'r', 'a', 'a', 'r', 'a'],
  ['a', 'g', 'a', 'g', 'a', 'g'],
  ['g', 'g', 'g', 'g', 'g', 'g'],
];

function TileViz({ row }: { row: Row }) {
  const meta = GAME_META[row.game];

  if (row.game === 'spot') {
    return (
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: row.image
            ? `center/cover no-repeat url("${row.image}")`
            : meta.color,
        }}
      />
    );
  }

  if (row.game === 'connections') {
    return (
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          gridTemplateRows: 'repeat(4, 1fr)',
          gap: 4,
          padding: 12,
        }}
      >
        {CONNECTIONS_BANDS.map((c) => (
          <div key={c} style={{ background: c, borderRadius: 3 }} />
        ))}
      </div>
    );
  }

  if (row.game === 'decode') {
    return (
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          gridTemplateRows: `repeat(${DECODE_HEAT.length}, 1fr)`,
          gap: 4,
          padding: 12,
          alignContent: 'center',
        }}
      >
        {DECODE_HEAT.map((heatRow, i) => (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${heatRow.length}, 1fr)`,
              gap: 4,
            }}
          >
            {heatRow.map((cell, j) => (
              <span
                key={j}
                style={{ background: DECODE_COLORS[cell], borderRadius: 3 }}
              />
            ))}
          </div>
        ))}
      </div>
    );
  }

  // Grid — single-colour motif grid.
  const m = MOTIF[row.game];
  const cells = Array.from({ length: m.cols * m.rows });
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        gridTemplateColumns: `repeat(${m.cols}, 1fr)`,
        gap: 4,
        padding: 12,
        alignContent: 'center',
      }}
    >
      {cells.map((_, i) => (
        <span
          key={i}
          style={{
            aspectRatio: '1 / 1',
            borderRadius: 3,
            background: meta.color,
            opacity: i % 2 === 0 ? 0.85 : 0.4,
          }}
        />
      ))}
    </div>
  );
}

function Tile({ row }: { row: Row }) {
  const meta = GAME_META[row.game];
  return (
    <Link
      href={meta.href(row.date)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid var(--border-soft)',
        borderRadius: 12,
        overflow: 'hidden',
        background: 'var(--card-bg)',
        textDecoration: 'none',
      }}
    >
      <div
        style={{
          position: 'relative',
          aspectRatio: '4 / 3',
          background: `color-mix(in srgb, ${meta.color} 14%, var(--card-bg))`,
        }}
      >
        <TileViz row={row} />
      </div>
      {/* Unplayed tiles get a solid game-colour footer (a "play me" cue);
          played tiles keep the plain footer and show the score in the same
          bottom slot, at matching height. */}
      <div
        style={{
          padding: '7px 10px',
          borderTop: '1px solid var(--border-soft)',
          ...(row.played
            ? {}
            : { background: meta.color, borderTopColor: 'transparent' }),
        }}
      >
        <div
          className={row.played ? 'games-tile-accent' : undefined}
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
            // Played: identity colour via the .games-tile-accent hook
            // (tokens.css), which brightens it on the dark card surface.
            ...(row.played
              ? { ['--tile-accent' as string]: meta.color }
              : {
                  color: '#fff',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                }),
          }}
        >
          {meta.label}
        </div>
        <div
          style={{
            fontSize: 11,
            color: row.played ? undefined : 'rgba(255,255,255,0.9)',
            opacity: row.played ? 0.6 : 1,
            textShadow: row.played ? undefined : '0 1px 2px rgba(0,0,0,0.25)',
          }}
        >
          {prettyDate(row.date)}
        </div>
        {row.game === 'decode' && row.played && row.label && (
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              marginTop: 3,
              color: 'var(--fg-1)',
            }}
          >
            {row.label}
          </div>
        )}
        {/* Bottom slot: the score (played) or the Play cue (unplayed), same
            font + size so both footers sit at the same height. */}
        <div
          className={row.played ? 'games-tile-accent' : undefined}
          style={{
            marginTop: 3,
            fontFamily: 'var(--font-display)',
            fontSize: 20,
            fontWeight: 700,
            lineHeight: 1.25,
            ...(row.played
              ? { ['--tile-accent' as string]: meta.color }
              : {
                  color: '#fff',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                }),
          }}
        >
          {row.played
            ? row.score != null
              ? `${row.score}/${scoreMax(row.game)}`
              : 'Done'
            : 'Play →'}
        </div>
      </div>
    </Link>
  );
}

type Filter = DailyGame | 'all';

// ?game= deep-link → initial filter. Unknown/absent (or grid while disabled) → 'all'.
function parseGameParam(v: string | null): Filter {
  if (v === 'spot' || v === 'connections' || v === 'decode' || v === 'duel')
    return v;
  if (v === 'grid' && GRID_ENABLED) return 'grid';
  return 'all';
}

function FilterBar({
  games,
  value,
  onChange,
}: {
  games: DailyGame[];
  value: Filter;
  onChange: (f: Filter) => void;
}) {
  const opts: { value: Filter; label: string }[] = [
    { value: 'all', label: 'All' },
    ...games.map((g) => ({ value: g as Filter, label: GAME_META[g].short })),
  ];
  return (
    <div
      role="tablist"
      aria-label="Filter by game"
      className="mo-bg-card"
      style={{
        display: 'inline-flex',
        border: '1px solid var(--border)',
        borderRadius: 999,
        overflow: 'hidden',
        boxShadow: 'var(--card-stack)',
      }}
    >
      {opts.map((opt, i) => {
        const on = value === opt.value;
        const accent = opt.value === 'all' ? null : GAME_META[opt.value].color;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(opt.value)}
            style={{
              padding: '7px 14px',
              border: 0,
              borderLeft: i > 0 ? '1px solid var(--border)' : 0,
              background: on
                ? (accent ?? 'var(--denim-100)')
                : 'transparent',
              color: on
                ? accent
                  ? '#fff'
                  : 'var(--denim-deep)'
                : 'var(--fg-2)',
              fontFamily: 'var(--font-sans)',
              fontSize: 12.5,
              fontWeight: on ? 600 : 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'background 120ms ease, color 120ms ease',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function GamesHistory({ userId }: { userId?: string }) {
  const searchParams = useSearchParams();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [filter, setFilter] = useState<Filter>(() =>
    parseGameParam(searchParams?.get('game') ?? null),
  );
  const [expanded, setExpanded] = useState(false);

  // Same-route /games?game=<g> deep-links (the on-page card "archive →" links)
  // keep this component mounted, so the lazy initializer above never re-runs.
  // Sync the filter to the ?game= param reactively. Pill clicks set `filter`
  // directly without touching the URL, so this effect doesn't re-fire on them.
  const gameParam = searchParams?.get('game') ?? null;
  useEffect(() => {
    setFilter(parseGameParam(gameParam));
    setExpanded(false);
  }, [gameParam]);

  useEffect(() => {
    const games = ALL_GAMES.filter((g) => g !== 'grid' || GRID_ENABLED);
    let cancelled = false;

    Promise.all(games.map((g) => fetchProgress(g, { userId }))).then(
      (results) => {
        if (cancelled) return;
        const merged: Row[] = [];
        results.forEach((p, i) => {
          if (!p) return;
          const game = games[i];
          for (const e of p.entries) {
            merged.push({
              game,
              date: e.date,
              played: e.played,
              score: e.score,
              solved: e.solved,
              label: e.label,
              image: e.image,
            });
          }
        });
        merged.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
        setRows(merged);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (!rows || rows.length === 0) return null;

  const games = ALL_GAMES.filter((g) => g !== 'grid' || GRID_ENABLED);
  const filtered =
    filter === 'all' ? rows : rows.filter((r) => r.game === filter);
  const visible = expanded ? filtered : filtered.slice(0, COLLAPSED_COUNT);

  const choose = (f: Filter) => {
    setFilter(f);
    setExpanded(false);
  };

  return (
    <section style={{ marginTop: 40 }}>
      <h2
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 20,
          textAlign: 'center',
          margin: '0 0 4px',
        }}
      >
        Play the back catalogue
      </h2>
      <p
        style={{
          textAlign: 'center',
          fontSize: 12.5,
          opacity: 0.66,
          margin: '0 0 16px',
        }}
      >
        {userId
          ? 'Across every device you play on — tap any day to play or replay'
          : 'Saved on this device — tap any day to play or replay'}
      </p>

      <div style={{ textAlign: 'center', margin: '0 0 18px' }}>
        <FilterBar games={games} value={filter} onChange={choose} />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: 12,
        }}
      >
        {visible.map((r) => (
          <Tile key={`${r.game}-${r.date}`} row={r} />
        ))}
      </div>

      {!expanded && filtered.length > COLLAPSED_COUNT && (
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="mo-bg-card"
            style={{
              padding: '9px 20px',
              border: '1px solid var(--border)',
              borderRadius: 999,
              boxShadow: 'var(--card-stack)',
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--fg-1)',
              cursor: 'pointer',
            }}
          >
            Show more ({filtered.length - COLLAPSED_COUNT} more)
          </button>
        </div>
      )}
    </section>
  );
}
